import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Share2, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function SharePageDialog({ page }) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const url = typeof window !== "undefined" ? `${window.location.origin}/p/${page.id}` : "";

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Could not copy");
        }
    };

    return (
        <>
            <button
                data-testid="viewer-share-button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-medical-900 rounded hover:bg-slate-100 transition-colors"
                title="Share this page"
            >
                <Share2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Share</span>
            </button>

            {open && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    data-testid="share-dialog"
                >
                    <div
                        className="absolute inset-0 bg-medical-900/40 backdrop-blur-sm"
                        onClick={() => setOpen(false)}
                    />
                    <div className="relative bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-md p-6">
                        <button
                            data-testid="share-dialog-close"
                            onClick={() => setOpen(false)}
                            className="absolute top-3 right-3 p-1.5 rounded hover:bg-slate-100"
                        >
                            <X className="h-4 w-4" />
                        </button>

                        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-teal-700 mb-1">
                            Share page
                        </div>
                        <h3 className="font-display text-xl font-bold text-medical-900 leading-tight pr-6 mb-1">
                            {page.title}
                        </h3>
                        <p className="text-xs font-mono text-slate-500 mb-5">
                            {page.category} · p.{String(page.page_number).padStart(3, "0")}
                        </p>

                        <div
                            className="flex justify-center bg-slate-50 border border-slate-200 rounded-lg py-6 mb-4"
                            data-testid="share-qr-wrapper"
                        >
                            <QRCodeSVG
                                value={url}
                                size={192}
                                level="M"
                                fgColor="#0F172A"
                                bgColor="#F8F9FA"
                                marginSize={2}
                            />
                        </div>

                        <p className="text-xs text-slate-500 mb-2 text-center">
                            Scan with a phone or tap to copy.
                        </p>
                        <button
                            data-testid="share-copy-link"
                            onClick={copy}
                            className="w-full h-10 inline-flex items-center justify-center gap-2 bg-teal-700 hover:bg-teal-800 text-white rounded-md font-medium transition-colors"
                        >
                            {copied ? (
                                <>
                                    <Check className="h-4 w-4" /> Copied
                                </>
                            ) : (
                                <>
                                    <Copy className="h-4 w-4" /> Copy link
                                </>
                            )}
                        </button>
                        <div
                            className="mt-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-[11px] font-mono text-slate-600 break-all"
                            data-testid="share-link-text"
                        >
                            {url}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
